import { z } from 'zod';
import { queryArray } from '../common/query-array';

export const FLEET_RUN_KINDS = ['ticket', 'report', 'idle'] as const;

/**
 * The one slug the lane's `--skill` filter and the stored `skill_slug` column
 * share, so the file reader and the ledger cannot disagree about which runs a
 * skill names. Mirrored body-for-body by `slugSegment` in
 * the fleet-agent toolkit's metrics, which cannot import this package — keep the two
 * identical.
 */
export function fleetSkillSlug(value: string): string {
  return value
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

export const FleetRunUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
});

export const FleetRunDecomposedUsageSchema = z.object({
  uncachedInputTokens: z.number().int().nonnegative(),
  cacheReadInputTokens: z.number().int().nonnegative(),
  cacheWriteInputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  usageStatus: z.enum(['COMPLETE', 'PARTIAL', 'MISSING']),
});

/**
 * Schema v1, as the fleet-agent toolkit (`metrics.ts`) writes it. The required set is
 * MEASURED against every record published to `main`; every other field is
 * missing from at least one of them (BL-039).
 */
export const FleetRunRecordSchema = z.object({
  // Not `z.literal(1)`: the version is checked on its own first, so a newer
  // agent's record reads as `unsupported-version` rather than as malformed.
  schemaVersion: z.number().int().positive(),
  runId: z.string().min(1).max(200),
  startedAt: z.iso.datetime(),
  at: z.iso.datetime(),
  durationMs: z.number().int().nonnegative(),
  kind: z.enum(FLEET_RUN_KINDS),
  worker: z.string().min(1).max(128),
  host: z.string().min(1).max(255),
  profile: z.string().min(1).max(64),
  sessionId: z.string().min(1).max(200),
  skill: z.string().max(200),
  engine: z.string().min(1).max(64),
  model: z.string().min(1).max(64),
  fingerprint: z.string().min(1).max(128),
  resumed: z.boolean(),
  usageParsed: z.boolean(),
  exit: z.object({ code: z.number().int().nullable(), signal: z.string().nullable() }),
  usage: FleetRunUsageSchema.nullable().optional(),
  decomposedUsage: FleetRunDecomposedUsageSchema.nullable().optional(),
  ticket: z.string().max(64).optional(),
  txn: z.string().max(200).optional(),
  member: z.string().max(200).optional(),
  workId: z.string().max(200).optional(),
  phase: z.string().max(200).optional(),
  jobOutcome: z.string().max(64).optional(),
  reportMessageId: z.string().max(200).optional(),
  buzzMessageId: z.string().max(200).optional(),
  claimedState: z.string().max(200).optional(),
  linearCommentId: z.string().max(200).optional(),
});

export type FleetRunRecord = z.infer<typeof FleetRunRecordSchema>;

export const FleetSessionRecordSchema = z.object({
  schemaVersion: z.number().int().positive(),
  sessionId: z.string().min(1).max(200),
  worker: z.string().min(1).max(128),
  host: z.string().min(1).max(255),
  profile: z.string().min(1).max(64),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().optional(),
  endReason: z.enum(['stop', 'restart', 'orphaned']).optional(),
  engine: z.object({ engine: z.string(), model: z.string().optional() }).optional(),
  totals: z.object({}).loose(),
  runIds: z.array(z.string()),
  tickets: z.array(z.string()),
  metricsBranch: z.string().max(400).optional(),
  pullRequestUrl: z.string().max(400).optional(),
});

export type FleetSessionRecord = z.infer<typeof FleetSessionRecordSchema>;

export const FleetRunTotalsQuerySchema = z.object({
  profile: z.string().min(1).max(64).optional(),
  worker: z.string().min(1).max(128).optional(),
  ticket: z.string().min(1).max(64).optional(),
  skill: z.string().min(1).max(200).optional(),
  /** `YYYY-MM-DD`, resolved to a half-open UTC day. */
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** `YYYY-Www`, resolved to a half-open UTC ISO week. */
  week: z
    .string()
    .regex(/^\d{4}-W\d{2}$/)
    .optional(),
});

export const FleetRunTotalsSchema = z.object({
  runs: z.number().int().nonnegative(),
  /** Of those runs, how many could not have their usage read. `runs - unparsedRuns` is what the sums cover. */
  unparsedRuns: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  uncachedInputTokens: z.number().int().nonnegative(),
  cacheReadInputTokens: z.number().int().nonnegative(),
  cacheWriteInputTokens: z.number().int().nonnegative(),
  decomposedOutputTokens: z.number().int().nonnegative(),
  lastRunAt: z.iso.datetime().nullable(),
  lastRunTokens: z.number().int().nonnegative().nullable(),
});

export type FleetRunTotalsQuery = z.infer<typeof FleetRunTotalsQuerySchema>;
export type FleetRunTotals = z.infer<typeof FleetRunTotalsSchema>;

/**
 * The token-metrics series (IMM-911 Metriken page): one read that answers every
 * view of the page — time buckets, up to two grouping levels, multi-value
 * filters and the period before — computed on read from `fleet_runs`.
 *
 * Buckets and the heatmap's hour/weekday are cut in ONE fixed zone, the
 * operators' own: a "day" is a Berlin calendar day, DST included. The client
 * sends `from`/`to` already on those boundaries.
 */
export const FLEET_METRICS_TIME_ZONE = 'Europe/Berlin';

export const FLEET_METRIC_BUCKETS = ['hour', 'day', 'week', 'month'] as const;
export const FLEET_METRIC_DIMENSIONS = ['host', 'profile', 'model'] as const;

export type FleetMetricBucket = (typeof FLEET_METRIC_BUCKETS)[number];
export type FleetMetricDimension = (typeof FLEET_METRIC_DIMENSIONS)[number];

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** The widest window each bucket may span, so no request asks for thousands of buckets. */
export const FLEET_METRIC_MAX_SPAN_MS: Readonly<Record<FleetMetricBucket, number>> = {
  hour: 8 * DAY_MS,
  day: 100 * DAY_MS,
  week: 3 * 366 * DAY_MS,
  month: 10 * 366 * DAY_MS,
};

export const FleetMetricsSeriesQuerySchema = z
  .object({
    /** Inclusive. */
    from: z.iso.datetime(),
    /** Exclusive. */
    to: z.iso.datetime(),
    bucket: z.enum(FLEET_METRIC_BUCKETS),
    /** `[panel, row]` — one or two distinct dimensions. */
    groupBy: queryArray(z.enum(FLEET_METRIC_DIMENSIONS)),
    profile: queryArray(z.string().min(1).max(64)).optional(),
    host: queryArray(z.string().min(1).max(255)).optional(),
    model: queryArray(z.string().min(1).max(64)).optional(),
    /** Start of the comparison window `[previousFrom, from)`; absent = no comparison. */
    previousFrom: z.iso.datetime().optional(),
    /** Weekday × hour per value of the first `groupBy` dimension. */
    heatmap: z.stringbool().default(false),
  })
  .refine((query) => Date.parse(query.from) < Date.parse(query.to), { message: 'from must be before to', path: ['to'] })
  .refine((query) => Date.parse(query.to) - Date.parse(query.from) <= FLEET_METRIC_MAX_SPAN_MS[query.bucket], {
    message: 'window too wide for this bucket',
    path: ['bucket'],
  })
  .refine((query) => query.groupBy.length >= 1 && query.groupBy.length <= 2, {
    message: 'groupBy takes one or two dimensions',
    path: ['groupBy'],
  })
  .refine((query) => new Set(query.groupBy).size === query.groupBy.length, {
    message: 'groupBy dimensions must differ',
    path: ['groupBy'],
  })
  .refine((query) => query.previousFrom === undefined || Date.parse(query.previousFrom) < Date.parse(query.from), {
    message: 'previousFrom must be before from',
    path: ['previousFrom'],
  });

/**
 * A run whose four token classes are unknown — `usageParsed: false`, or a
 * record written before the decomposition existed — counts in `runs`,
 * `unparsedRuns` and `durationMs`, never in the token fields. The UI derives
 * `—` (every run unparsed) and `≈` (some run unparsed) from the two counts.
 */
export const FleetMetricAggregateSchema = z.object({
  runs: z.number().int().nonnegative(),
  unparsedRuns: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  uncachedInputTokens: z.number().int().nonnegative(),
  cacheReadInputTokens: z.number().int().nonnegative(),
  cacheWriteInputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
});

const FleetMetricKeySchema = z.object({
  host: z.string().optional(),
  profile: z.string().optional(),
  model: z.string().optional(),
});

const FleetMetricGroupSchema = z.object({
  key: FleetMetricKeySchema,
  total: FleetMetricAggregateSchema,
  /** `null` when the comparison window is not asked for or lies before the first run. */
  previous: FleetMetricAggregateSchema.nullable(),
  /** One entry per bucket; `null` = no run in that bucket, which is not an aggregate of zeros. */
  series: z.array(FleetMetricAggregateSchema.nullable()),
});

export const FleetMetricsSeriesSchema = z.object({
  timeZone: z.string(),
  buckets: z.array(z.object({ start: z.iso.datetime(), end: z.iso.datetime() })),
  total: FleetMetricAggregateSchema,
  previous: FleetMetricAggregateSchema.nullable(),
  /** Grouped by the first `groupBy` dimension only — the panel sums, including the previous window's. */
  panels: z.array(FleetMetricGroupSchema),
  /** Grouped by every `groupBy` dimension — the rows. With one dimension, the same as `panels`. */
  groups: z.array(FleetMetricGroupSchema),
  /** `[Mon..Sun][0..23]`, present when asked for. */
  heatmap: z
    .array(
      z.object({
        key: z.string(),
        cells: z.array(z.array(FleetMetricAggregateSchema.nullable()).length(24)).length(7),
      }),
    )
    .optional(),
  /** The earliest run in the whole ledger, unfiltered — the floor paging stops at. */
  firstRunAt: z.iso.datetime().nullable(),
});

export type FleetMetricsSeriesQuery = z.infer<typeof FleetMetricsSeriesQuerySchema>;
export type FleetMetricAggregate = z.infer<typeof FleetMetricAggregateSchema>;
export type FleetMetricKey = z.infer<typeof FleetMetricKeySchema>;
export type FleetMetricGroup = z.infer<typeof FleetMetricGroupSchema>;
export type FleetMetricsSeries = z.infer<typeof FleetMetricsSeriesSchema>;
